import { FC, useCallback } from 'react';
import graphql from 'babel-plugin-relay/macro';

import FmdGoodIcon from '@mui/icons-material/FmdGood';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableHeadCell from 'components/TableHeadCell';
import TableCell from 'components/TableCell';
import FabDialog from 'components/FabDialog';
import PoiListRow from 'components/PoiListRow';
import PoiListAdd from 'components/PoiListAdd';

import { useSearch } from 'components/Search';
import { usePagination } from 'components/Pagination';
import { useAvoidSuspense, useDelete, useSkipFirstLoad } from 'lib';

import Query, {
  PoiListRefetchQuery,
} from '__generated__/PoiListRefetchQuery.graphql';
import { PoiList_data$key } from '__generated__/PoiList_data.graphql';

type Props = {
  data: PoiList_data$key;
};

const PoiList: FC<Props> = props => {
  const { search } = useSearch();
  const { variables } = usePagination();

  const [{ pois }, { refetch, loading }] = useAvoidSuspense<
    PoiListRefetchQuery,
    PoiList_data$key
  >(
    graphql`
      fragment PoiList_data on Query
      @refetchable(queryName: "PoiListRefetchQuery")
      @argumentDefinitions(
        first: { type: "Int!" }
        after: { type: "String" }
        search: { type: "String" }
      ) {
        pois(first: $first, after: $after, search: $search) {
          __id
          edges {
            node {
              id

              ...PoiListRow_poi
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }
      }
    `,
    props.data,
    Query
  );

  const connectionId = pois?.__id;

  const [deleteWhatToSee, deleting] = useDelete(connectionId);

  const { edges, pageInfo } = pois ?? {};

  useSkipFirstLoad(
    useCallback(() => {
      refetch({
        ...variables,
        search,
      });
    }, [refetch, search, variables])
  );

  return (
    <>
      <Table
        search
        pagination
        pageInfo={pageInfo ?? null}
        icon={<FmdGoodIcon />}
        loading={loading || deleting}
        head={
          <TableRow>
            <TableHeadCell name="name">Pavadinimas</TableHeadCell>
            <TableCell />
          </TableRow>
        }>
        {edges?.map(({ node }) => (
          <PoiListRow key={node.id} poi={node} onDelete={deleteWhatToSee} />
        ))}
      </Table>

      {connectionId && (
        <FabDialog fullWidth maxWidth="sm">
          <PoiListAdd connectionID={connectionId} />
        </FabDialog>
      )}
    </>
  );
};

export default PoiList;
